import React, { useMemo } from 'react';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import type { StrapiSamplingScheduleRow } from '../api/strapiCrm';
import type { DynamicEntityRow } from '../api/strapiDynamicEntity';
import {
  buildGroupLabelForSchedule,
  groupIndexByCreationOrder,
  scheduleDisplayCustomerName,
} from '../pages/SamplingSchedulesPage/samplingSchedulePlans';
import { quotationUsesSampleGroups } from '../pages/QuotationsPage/quotationWorkflow';
import { DataGridNumberedPagination, DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS } from './DataGridNumberedPagination';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  scheduling_in_progress: 'Penjadwalan',
  rejected: 'Ditolak',
  approved: 'Disetujui',
  work_order_created: 'WO dibuat',
  cancelled: 'Dibatalkan',
};

type SamplingScheduleDataGridProps = {
  rows: StrapiSamplingScheduleRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  onView: (row: StrapiSamplingScheduleRow) => void;
};

export function SamplingScheduleDataGrid({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  isLoading,
  isError,
  error,
  onView,
}: SamplingScheduleDataGridProps) {
  const columns: GridColDef<StrapiSamplingScheduleRow>[] = useMemo(
    () => [
      {
        field: 'schedule_number',
        headerName: 'No. jadwal',
        minWidth: 130,
        flex: 0.55,
        valueFormatter: (v) => (v ? String(v) : '—'),
      },
      {
        field: 'quotation',
        headerName: 'Quotation',
        minWidth: 130,
        flex: 0.5,
        sortable: false,
        valueGetter: (_, row) => row.quotation?.quotation_number ?? '—',
      },
      {
        field: 'sample_group',
        headerName: 'Group sample',
        minWidth: 160,
        flex: 0.65,
        sortable: false,
        valueGetter: (_, row) => {
          if (!row.quotation || !quotationUsesSampleGroups(row.quotation)) return '—';
          const quotationDocId = String(row.quotation.documentId ?? '').trim();
          const groupDocId = String(row.quotation_group?.documentId ?? '').trim();
          const groupIndexById = groupIndexByCreationOrder(
            rows
              .filter(
                (r) => String(r.quotation?.documentId ?? '').trim() === quotationDocId,
              )
              .map((r) => ({
                documentId: r.quotation_group?.documentId,
                createdAt: r.quotation_group?.createdAt,
              })),
          );
          const groupIndex = groupDocId ? (groupIndexById.get(groupDocId) ?? 0) : 0;
          const scheduleRow = {
            documentId: row.documentId,
            quotation_group: row.quotation_group,
          } as DynamicEntityRow;
          return buildGroupLabelForSchedule(scheduleRow, row.quotation, groupIndex) ?? '—';
        },
      },
      {
        field: 'customer',
        headerName: 'Pelanggan',
        minWidth: 160,
        flex: 1,
        sortable: false,
        valueGetter: (_, row) => scheduleDisplayCustomerName(row) || '—',
      },
      {
        field: 'schedule_type',
        headerName: 'Tipe',
        minWidth: 90,
        flex: 0.35,
        valueFormatter: (v) => (v ? String(v) : '—'),
      },
      {
        field: 'schedule_kind',
        headerName: 'Jenis',
        minWidth: 120,
        flex: 0.4,
        valueGetter: (_, row) =>
          (row.schedule_kind ?? 'initial') === 'point_reschedule'
            ? `Reschedule T${row.reschedule_point_index ?? '?'}`
            : 'Awal',
      },
      {
        field: 'progress_status',
        headerName: 'Status',
        minWidth: 120,
        flex: 0.4,
        valueFormatter: (v) => STATUS_LABEL[String(v ?? '')] ?? String(v ?? '—'),
      },
      {
        field: 'work_order',
        headerName: 'GL / WO',
        minWidth: 110,
        flex: 0.4,
        sortable: false,
        valueGetter: (_, row) => row.work_order?.job_number ?? '—',
      },
      {
        field: '__actions',
        headerName: 'Aksi',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 56,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Box
            sx={{ display: 'flex', gap: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <IconButton
                size="small"
                aria-label={`Lihat penjadwalan ${row.schedule_number ?? row.documentId}`}
                title="Lihat penjadwalan"
                onClick={() => onView(row)}
                data-testid={`sampling-schedule-view-${row.documentId}`}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
          </Box>
        ),
      },
    ],
    [onView, rows],
  );

  if (isError) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Gagal memuat penjadwalan.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="sampling-schedules-list">
      {isLoading && rows.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.documentId}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[...DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS]}
          loading={isLoading}
          slots={{ pagination: DataGridNumberedPagination }}
          autoHeight
          density="compact"
          sx={{ '& .MuiDataGrid-columnHeaders': { fontWeight: 600 } }}
        />
      )}
    </Box>
  );
}
